import React, { useRef, useState, useEffect } from 'react';
// @ts-ignore
import styles from './Chart.module.scss';
import Text from '../../Base/Text/Text';
import LineChart from '../../Chart/LineChart';
import NetteApiClient from '../../../Services/NetteApiClient';
import { useAppContext } from '../../../Services/AppContext';
import { useEffectAsync } from '../../../helpers';
import apiClient from '../../../Services/ApiClient';
import { USER_HEALTH_DATA } from '../../UserInfo/UserData';
import { format } from 'date-fns';
// @ts-ignore
import moment from 'moment';
import { formatQueryString } from '../../../Services/Api/FormatQueryString';
import useScrollbar from '../../../Hooks/useScrollbar.hook';

interface IWeightChart {
	patientId: string;
	allowShowMore?: boolean;
	interval: string;
	source: string[];
	from: string | any;
	to: string | any;
	dataFormat?: string;
}

export default function WeightChart(props: IWeightChart) {
	const {
		patientId,
		allowShowMore = false,
		from,
		to,
		source,
		interval,
		dataFormat = 'default',
	} = props;
	const { showHealthData, translation } = useAppContext();
	const [isLoading, setIsLoading] = useState(false);
	const [showDefaultAxes, setShowDefaultAxes] = useState(true);
	const {
		range,
		setRange,
		setMaxScroll,
		setScrollOffset,
		scrollOffset,
		handleScroll,
		handleRecalculateRange,
		maxScroll,
	} = useScrollbar({
		from,
		to,
		interval,
	});

	const defaultOptions = {
		scales: {
			y: {
				beginAtZero: true,
				ticks: {
					fontSize: 18,
				},
				max: 100,
				min: 50,
				step: 5,
			},
		},
		responsive: true,
		plugins: {
			legend: {
				position: 'top',
			},
		},
		maintainAspectRatio: false,
	};

	const chartOptions = {
		scales: {
			y: {
				beginAtZero: true,
				title: {
					display: true,
					text: 'kg',
					color: '#1665d8',
					fontWeight: 500,
				},
				ticks: {
					fontSize: 18,
				},
				step: 10,
			},
		},
		responsive: true,
		plugins: {
			legend: {
				position: 'top',
			},
			tooltip: {
				callbacks: {
					label: function (context: any) {
						const unit = 'kg';
						const value = context.formattedValue + ' ' + unit;
						return value;
					},
				},
			},
		},
		maintainAspectRatio: false,
	};

	useEffect(() => {
		const min = moment(from);
		const max = moment(to);
		const maxHours = max.diff(min, 'hours') * 10;

		setMaxScroll(maxHours);

		setScrollOffset(0);

		if (scrollOffset === 0) {
			handleRecalculateRange();
		}
	}, [from, to, source, interval, dataFormat]);

	const [chartData, setChartData] = useState({
		labels: [],
		datasets: [
			{
				label: translation['base.shared.form.weight.label2'],
				fill: true,
				backgroundColor: 'rgba(58, 150, 253, 0.05)',
				borderWidth: 2,
				pointRadius: 3,
				borderColor: '#3a96fd',
				pointBackgroundColor: '#ffffff',
			},
		],
	});

	const handleShowMore = () => {
		NetteApiClient.get(showHealthData);
	};

	// @ts-ignore
	useEffectAsync(async () => {
		if (!range?.from || !range?.to) {
			return;
		}

		setIsLoading(true);

		const tempLabels = [];
		const tempData = {
			labels: tempLabels,
			datasets: [
				{
					label: translation['base.shared.form.weight.label2'],
					fill: true,
					backgroundColor: 'rgba(58, 150, 253, 0.05)',
					borderWidth: 2,
					pointRadius: 3,
					borderColor: '#3a96fd',
					pointBackgroundColor: '#ffffff',
					data: [],
				},
			],
		};
		if (from && to && patientId && interval) {
			const query = formatQueryString({
				view: interval,
				start: range.from.toISOString(),
				end: range.to.toISOString(),
				keys: ['weight'],
				type: dataFormat,
			});
			const { data } = await apiClient.get(
				'/user/' + patientId + '/health-data/chart?' + query,
			);
			for (const row of data[0].data) {
				let value: string | number = 'NaN';
				if (row.value > 0) {
					value = row.value;
					setShowDefaultAxes(false);
				}
				switch (interval) {
					case 'day':
						tempLabels.push(format(new Date(row.time), 'HH:mm'));
						break;
					case 'week':
						tempLabels.push(format(new Date(row.time), 'dd.MM'));
						break;
					default:
						tempLabels.push(format(new Date(row.time), 'dd.MM.yyyy HH:mm'));
						break;
				}
				tempData.datasets[0].data.push(value);
			}
		}
		setChartData(tempData);
	}, [range]);

	useEffect(() => {
		setIsLoading(false);
	}, [chartData]);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<Text
					className={styles.title}
					tx={'base.shared.form.weight.label2'}
					firstUpper
				/>
				{allowShowMore && (
					<div className={styles.button} onClick={handleShowMore}>
						<Text tx={'base.showMore'} firstUpper />
					</div>
				)}
			</div>
			<LineChart
				data={chartData}
				isLoading={isLoading}
				scrollbar
				onScroll={handleScroll}
				scrollbarWidth={maxScroll}
				options={showDefaultAxes ? defaultOptions : chartOptions}
			/>
		</div>
	);
}
